import { z } from "zod";
import { BaseIdSchema, TimestampSchema } from "./common";

export const QuizIdSchema = BaseIdSchema;
export type QuizId = z.infer<typeof QuizIdSchema>;
export function validateQuizId(data: unknown): QuizId {
  return QuizIdSchema.parse(data);
}

/** Quiz type definitions */
export enum QuizTypeEnum {
  SINGLE = "single",
  MULTIPLE = "multiple",
  SCALE = "scale",
  DEGREE = "degree",
}

/** Quiz Meta schema */
export const QuizMetaSchema = z.object({
  question: z.string(),
  options: z.array(z.string()).optional(),
  scaleRange: z
    .object({
      min: z.number(),
      max: z.number(),
      labels: z.array(z.string()),
    })
    .optional(),
  degreeRange: z
    .object({
      min: z.number(),
      max: z.number(),
      labels: z.array(z.string()),
    })
    .optional(),
});
export type QuizMeta = z.infer<typeof QuizMetaSchema>;

/** Collection: quizzes
 *  quizId -> quiz
 */
export const QuizSchema = z.object({
  id: QuizIdSchema,
  type: z.nativeEnum(QuizTypeEnum),
  order: z.number().int().min(0),
  category: z.string(),
  active: z.boolean(),
  meta: z.record(z.enum(["en", "vi"]), QuizMetaSchema),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Quiz = z.infer<typeof QuizSchema>;
export function validateQuiz(data: unknown): Quiz {
  return QuizSchema.parse(data);
}

// Based on quiz type
// Single choice: z.number()
// Multiple choice: z.array(z.number())
// Scale: z.number()
// Degree: z.number()
export const QuizGeneralAnswerSchema = z.union([
  z.number(),
  z.array(z.number()),
  z.number(),
]);

export const QuizAnswerSchema = z.object({
  id: QuizIdSchema,
  answer: QuizGeneralAnswerSchema,
});
export type QuizAnswer = z.infer<typeof QuizAnswerSchema>;
export function validateQuizAnswer(data: unknown): QuizAnswer {
  return QuizAnswerSchema.parse(data);
}

export const QuizCompletionSchema = z.object({
  userId: BaseIdSchema,
  completed: z.boolean(),
  completedAt: TimestampSchema.optional(),
  score: z.number().min(0).max(100).optional(),
  answers: z.record(QuizIdSchema, QuizGeneralAnswerSchema),
  lastUpdated: TimestampSchema,
});
export type QuizCompletion = z.infer<typeof QuizCompletionSchema>;
export function validateQuizCompletion(data: unknown): QuizCompletion {
  return QuizCompletionSchema.parse(data);
}
